import { useMemo, useRef } from 'react';
import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { useIsElementVisible } from '@interxfi/design-system/util/visibility';
import { type IxcRegistrationV1ControlPersonDirector, IxcRegistrationV1ProfileStatus } from '@interxfi/types/generated/api';
import {
  buildProfileListQuery,
  RegistrationProfileComboboxWithLens,
} from '../../../../../../components/data-combobox-input/entity-config/registration-profile';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';

export const directorControlPersonFormSchema = z.object({
  kind: z.literal('director'),
  value: z.object({
    profileId: z.string().nonempty('Profile is required'),
    title: z.string(),
    signer: z.boolean(),
  }),
  primaryContact: z.boolean(),
});

export type DirectorControlPersonFormValues = z.infer<typeof directorControlPersonFormSchema>;

export const directorControlPersonFormDefaultValues = {
  kind: 'director',
  value: {
    profileId: '',
    title: '',
    signer: false,
  },
  primaryContact: false,
} as const satisfies DirectorControlPersonFormValues;

export function formValuesToDirectorControlPerson(values: DirectorControlPersonFormValues): IxcRegistrationV1ControlPersonDirector {
  return addDefinedValues<IxcRegistrationV1ControlPersonDirector>(
    { profileId: values.value.profileId, signer: values.value.signer },
    { title: values.value.title },
  );
}

export function getInitialFormValuesForDirectorControlPerson(
  data: IxcRegistrationV1ControlPersonDirector | undefined,
  primaryContactId: string | undefined,
): DirectorControlPersonFormValues {
  return {
    ...directorControlPersonFormDefaultValues,
    value: {
      ...directorControlPersonFormDefaultValues.value,
      profileId: data?.profileId ?? '',
      title: data?.title ?? '',
      signer: Boolean(data?.signer),
    },
    primaryContact: Boolean(data?.profileId && data.profileId === primaryContactId),
  };
}

interface DirectorControlPersonFormProps {
  lens: Lens<DirectorControlPersonFormValues>;
  programId: string | undefined;
}

export function DirectorControlPersonForm({ lens, programId }: DirectorControlPersonFormProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsElementVisible(ref);
  const profileSelectRequest = useMemo(() => {
    if (!programId) {
      return null;
    }

    return buildProfileListQuery({ programId, status: IxcRegistrationV1ProfileStatus.Active });
  }, [programId]);

  return (
    <VerticalStack gap={4} ref={ref}>
      <RegistrationProfileComboboxWithLens
        label="Profile"
        lens={lens.focus('value.profileId')}
        queryOptions={profileSelectRequest}
        required={isVisible}
      />

      <SwitchInputGroup>
        <AppSwitchInputWithLens label="Signer" lens={lens.focus('value.signer')} />
        <AppSwitchInputWithLens label="Primary Contact" lens={lens.focus('primaryContact')} />
      </SwitchInputGroup>

      <AppTextInputWithLens label="Title" lens={lens.focus('value.title')} />
    </VerticalStack>
  );
}
